import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  createMenuCategory,
  updateMenuCategory,
  deleteMenuCategory,
  reorderMenuCategories,
  getDefaultMenuCategories,
} from "@/lib/api/admin";
import type { MenuCategory } from "@/lib/api/types";
import { isDuplicateCategoryName, DUPLICATE_CATEGORY_NAME_ERROR } from "@/lib/utils/menu";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/Textarea";
import { CategoryNameInput } from "@/components/CategoryNameInput";

// ─── Categories editor (ManageCategoriesDialog body) ──────────────────────────

type CategoriesEditorProps = {
  storeId: string;
  categories: MenuCategory[];
  onRefresh: () => void;
};

export function CategoriesEditor({ storeId, categories, onRefresh }: CategoriesEditorProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [createError, setCreateError] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [renameError, setRenameError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<MenuCategory | null>(null);
  const [ordered, setOrdered] = useState<MenuCategory[]>(categories);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const { data: defaultCategories = [] } = useQuery({
    queryKey: ["default-menu-categories"],
    queryFn: () => getDefaultMenuCategories(user!),
    staleTime: Infinity,
  });
  const defaultCategoryNames = defaultCategories.map((c) => c.name);

  // Sync ordered list only when the ID set changes, not on rename, so updated_at ordering doesn't shuffle it.
  const categoriesKey = [...categories]
    .map((c) => c.id)
    .sort()
    .join(",");
  const [prevCategoriesKey, setPrevCategoriesKey] = useState(categoriesKey);
  if (categoriesKey !== prevCategoriesKey) {
    setPrevCategoriesKey(categoriesKey);
    setOrdered(categories);
  }

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["menu-categories", storeId] });
    onRefresh();
  }

  const createMutation = useMutation({
    mutationFn: async () =>
      createMenuCategory(user!, storeId, newName.trim(), newDescription.trim()),
    onSuccess: () => {
      invalidate();
      setNewName("");
      setNewDescription("");
      toast.success("Category created.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      displayOrder,
    }: {
      id: string;
      name: string;
      description: string;
      displayOrder: number;
    }) => updateMenuCategory(user!, storeId, id, { name, description, displayOrder }),
    onSuccess: (_, { id, name, description }) => {
      setOrdered((prev) => prev.map((c) => (c.id === id ? { ...c, name, description } : c)));
      setEditingId(null);
      invalidate();
      toast.success("Category updated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => deleteMenuCategory(user!, storeId, id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      toast.success("Category deleted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  function submitCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (isDuplicateCategoryName(trimmed, ordered)) {
      setCreateError(DUPLICATE_CATEGORY_NAME_ERROR);
      return;
    }
    createMutation.mutate();
  }

  function submitRename(id: string) {
    const trimmed = editName.trim();
    if (!trimmed) return;
    if (isDuplicateCategoryName(trimmed, ordered, id)) {
      setRenameError(DUPLICATE_CATEGORY_NAME_ERROR);
      return;
    }
    const displayOrder = ordered.find((c) => c.id === id)?.display_order ?? 0;
    updateMutation.mutate({
      id,
      name: trimmed,
      description: editDescription.trim(),
      displayOrder,
    });
  }

  function startEdit(cat: MenuCategory) {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditDescription(cat.description);
    setRenameError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setRenameError("");
  }

  const reorderMutation = useMutation({
    mutationFn: async (ids: string[]) => reorderMenuCategories(user!, storeId, ids),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["menu-categories", storeId] }),
    onError: () => {
      setOrdered(categories);
      toast.error("Failed to reorder categories.");
    },
  });

  function handleDragStart(index: number) {
    setDragIndex(index);
  }

  function handleDragOver(e: React.DragEvent, index: number) {
    e.preventDefault();
    setOverIndex(index);
  }

  function handleDrop(e: React.DragEvent, index: number) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...ordered];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setOrdered(next);
    setDragIndex(null);
    setOverIndex(null);
    reorderMutation.mutate(next.map((c) => c.id));
  }

  function handleDragEnd() {
    setDragIndex(null);
    setOverIndex(null);
  }

  return (
    // min-w-0: DialogContent is a grid, or a long description grows the dialog instead of truncating.
    <div className="space-y-4 min-w-0">
      {/* Create */}
      <form onSubmit={submitCreate} className="space-y-2">
        <div className="flex gap-2">
          <CategoryNameInput
            className="flex-1"
            placeholder="New category name"
            value={newName}
            onChange={(v) => {
              setNewName(v);
              if (createError) setCreateError("");
            }}
            onSelectSuggestion={() => setCreateError("")}
            suggestions={defaultCategoryNames}
            existingNames={ordered.map((c) => c.name)}
            aria-invalid={!!createError}
            disabled={createMutation.isPending}
          />
          <Button type="submit" disabled={createMutation.isPending || !newName.trim()}>
            {createMutation.isPending ? (
              <Spinner className="size-4 border-primary-foreground mr-1" />
            ) : (
              <Plus className="size-4 mr-1" />
            )}
            Add
          </Button>
        </div>
        {createError && <p className="text-xs text-destructive">{createError}</p>}
        <Textarea
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          placeholder="Description (optional) — shown to customers under this category"
          maxLength={700}
          rows={2}
          className="text-sm"
          disabled={createMutation.isPending}
        />
      </form>

      {/* List */}
      {ordered.length === 0 ? (
        <EmptyState title="No categories" description="Add a category above." />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">Drag to reorder.</p>
          <ul className="divide-y rounded-md border">
            {ordered.map((cat, i) => (
              <li
                key={cat.id}
                draggable
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={(e) => handleDrop(e, i)}
                onDragEnd={handleDragEnd}
                className={`transition-colors ${overIndex === i ? "bg-primary/5" : ""}`}
              >
                <div className="flex items-start gap-2 p-3">
                  <GripVertical className="size-4 text-muted-foreground cursor-grab shrink-0 mt-1.5" />
                  {editingId === cat.id ? (
                    <div className="flex-1 space-y-2 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <Input
                          className="h-7 text-sm sm:flex-1"
                          value={editName}
                          onChange={(e) => {
                            setEditName(e.target.value);
                            if (renameError) setRenameError("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") submitRename(cat.id);
                            if (e.key === "Escape") cancelEdit();
                          }}
                          maxLength={100}
                          aria-invalid={!!renameError}
                          autoFocus
                        />
                        <div className="flex gap-2 sm:contents">
                          <Button
                            size="sm"
                            className="h-7 flex-1 sm:flex-none"
                            onClick={() => submitRename(cat.id)}
                            disabled={updateMutation.isPending}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 flex-1 sm:flex-none"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                      <p
                        className={`text-xs text-right ${
                          editName.length > 100 ? "text-destructive" : "text-muted-foreground"
                        }`}
                      >
                        {editName.length}/100
                      </p>
                      <Textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description (optional) — shown to customers under this category"
                        maxLength={700}
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{cat.name}</p>
                        {cat.description && (
                          <p className="text-xs text-muted-foreground truncate">
                            {cat.description}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        onClick={() => startEdit(cat)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 text-muted-foreground enabled:hover:text-destructive enabled:hover:bg-destructive/10"
                        onClick={() => setDeleteTarget(cat)}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </>
                  )}
                </div>
                {editingId === cat.id && renameError && (
                  <p className="text-xs text-destructive px-3 pb-1">{renameError}</p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete category"
        description={`Delete "${deleteTarget?.name}"? Menu items in this category will become uncategorized.`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </div>
  );
}
